import { useForm } from "react-hook-form";
import {
  DMModal,
  DMModalContent,
  DMModalFooter,
  DMModalTitle,
} from "../../../shared/DMModal";
import Button from "../../../student/components/generic/button";
import { SnnSchool } from "./types";
import { useEffect, useState } from "react";
import {
  getProvinces,
  getStates,
  isValidZipCode,
} from "../../../shared/regionsUtils";
import { useMutation } from "@tanstack/react-query";
import { executeQuery } from "../../../utils";

export const EditSchoolModal: React.FC<{
  school: SnnSchool | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ school, onClose, onSaved }) => {
  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    getValues,
  } = useForm<SnnSchool>({
    defaultValues: school || {},
  });
  const [nonFieldError, setNonFieldError] = useState<string | null>(null);

  const states = getStates().map(({ key }) => key);
  const provinces = getProvinces().map(({ key }) => key);

  const { mutate, isPending } = useMutation({
    mutationFn: (data: SnnSchool) => {
      return executeQuery({
        path: "/manager_new/schools/snn/edit",
        method: "post",
        requestBody: data as unknown as Record<string, unknown>,
      });
    },
  });

  const onSubmit = (data: SnnSchool) => {
    setNonFieldError(null);
    mutate(data, {
      onSuccess: () => onSaved(),
      onError: (error: any) => {
        if (error.error) {
          setNonFieldError(error.error);
        } else if (error.message) {
          setNonFieldError(error.message);
        } else {
          setNonFieldError("An unknown error occurred");
        }
      },
    });
  };

  // Reset the form when the school changes
  useEffect(() => {
    if (school) {
      reset(school);
    }
  }, [reset, school]);

  return (
    <DMModal visible={school !== null} onClose={onClose}>
      <DMModalTitle>Edit school</DMModalTitle>
      <form
        className="flex w-full flex-col gap-10"
        onSubmit={handleSubmit(onSubmit)}
      >
        <DMModalContent>
          {nonFieldError && (
            <p className="w-full -translate-y-4 rounded bg-dm-error-100 p-2 text-center text-sm text-dm-error-500">
              {nonFieldError}
            </p>
          )}
          <div className="flex w-full flex-col gap-1">
            <label className="text-sm font-bold" htmlFor="schoolName">
              School name
            </label>
            <input
              className="h-10 w-full rounded border border-dm-charcoal-500 px-3 py-0"
              id="schoolName"
              {...register("schoolName", {
                required: "This field is required",
              })}
            />
            {errors.schoolName && (
              <p className="text-xs text-dm-error-500">
                {errors.schoolName.message}
              </p>
            )}
          </div>

          <div className="flex w-full flex-col gap-1">
            <label className="text-sm font-bold" htmlFor="street">
              Street
            </label>
            <input
              className="h-10 w-full rounded border border-dm-charcoal-500 px-3 py-0"
              id="street"
              {...register("street")}
            />
            {errors.street && (
              <p className="text-sm text-dm-error-500">
                {errors.street.message}
              </p>
            )}
          </div>

          <div className="flex w-full flex-col gap-1">
            <label className="text-sm font-bold" htmlFor="city">
              City
            </label>
            <input
              className="h-10 w-full rounded border border-dm-charcoal-500 px-3 py-0"
              id="city"
              {...register("city")}
            />
            {errors.city && (
              <p className="text-sm text-dm-error-500">{errors.city.message}</p>
            )}
          </div>

          <div className="flex gap-4">
            <div className="flex w-full flex-col gap-1">
              <label className="text-sm font-bold" htmlFor="state">
                State/province
              </label>
              <input
                className="h-10 w-full rounded border border-dm-charcoal-500 px-3 py-0"
                id="state"
                {...register("state")}
              />
              {errors.state && (
                <p className="text-sm text-dm-error-500">
                  {errors.state.message}
                </p>
              )}
            </div>

            <div className="flex w-full flex-col gap-1">
              <label className="text-sm font-bold" htmlFor="zip">
                Zip code
              </label>
              <input
                className="h-10 w-full rounded border border-dm-charcoal-500 px-3 py-0"
                id="zip"
                {...register("zip", {
                  validate: (value) => {
                    if (!value) {
                      return true;
                    }
                    const country = getValues("country");
                    if (country === "US" && !isValidZipCode(value, "US")) {
                      return "This does not look like a valid US zip code";
                    }
                    if (country === "CA" && !isValidZipCode(value, "CA")) {
                      return "This does not look like a valid Canadian postal code";
                    }
                    return true;
                  },
                })}
              />
              {errors.zip && (
                <p className="text-sm text-dm-error-500">
                  {errors.zip.message}
                </p>
              )}
            </div>

            <div className="flex w-full flex-col gap-1">
              <label className="text-sm font-bold" htmlFor="country">
                Country
              </label>
              <input
                className="h-10 w-full rounded border border-dm-charcoal-500 px-3 py-0"
                id="country"
                {...register("country", {
                  validate: {
                    validateUs: (value) => {
                      const state = getValues("state");

                      return state &&
                        value &&
                        states.includes(state) &&
                        value !== "US"
                        ? 'You picked a US state, but did not enter "US" for country'
                        : true;
                    },
                    validateCa: (value) => {
                      const province = getValues("state");
                      return province &&
                        value &&
                        provinces.includes(province) &&
                        value !== "CA"
                        ? 'You picked a Canadian province, but did not enter "CA" for country'
                        : true;
                    },
                    validateLength: (value) =>
                      value && value.length !== 2
                        ? "Country code must be 2 characters"
                        : true,
                  },
                })}
              />
              {errors.country && (
                <p className="text-sm text-dm-error-500">
                  {errors.country.message}
                </p>
              )}
            </div>
          </div>
        </DMModalContent>
        <DMModalFooter>
          <Button type="outline" onClick={onClose} submit={false}>
            Cancel
          </Button>
          <Button isLoading={isPending}>Save</Button>
        </DMModalFooter>
      </form>
    </DMModal>
  );
};
